import Tooltip from "../kit-mdtw/tooltip/Tooltip";
import { IDetalleCedulaItem } from "../../../interfaces/ISeleccion";
import { textoDetalleCedula } from "../../../utils/detalleCedula";

/**
 * Validación de la cédula, presentada como columna con etiqueta corta y
 * estilo pill (igual que "Estado"). Antes vivía solo como ícono al lado del
 * nombre; ahora es columna independiente ("Validación") en la
 * tabla de postulaciones y en las tablas de Selección Masiva y Profesional.
 *
 * POR QUÉ VIVE ACÁ Y NO EN CADA TABLA: el mismo dato se muestra en seis
 * tablas de dos pantallas. Si cada una declarara sus colores y rótulos,
 * alcanzaría con tocar uno para que las pantallas digan cosas distintas
 * sobre el mismo registro.
 *
 * Los veredictos son los que produce el motor, más "validado_manual", que NO
 * lo produce el motor sino un reclutador cuando resuelve un caso a mano
 * (corrige un dato/documento o confirma que ya estaba bien). "Pendiente" cubre
 * el `null` de la ficha — los registros que nunca pasaron por la validación —
 * que el ícono omitía pero que como columna sí conviene rotular.
 *
 * "APROBADO" se trata igual que "validado": es el mismo desenlace (los datos
 * calzan) con otro rótulo, así que comparte el pill verde.
 */
export interface ICedulaBadgeConfig {
  label: string;
  bg: string;
  color: string;
  icon: string;
}

const CEDULA_BADGE_CONFIG: Record<string, ICedulaBadgeConfig> = {
  ok:        { label: "Validado IA", bg: "#e7f5ec", color: "#1e7a45", icon: "check_circle"    },
  corregidoIA: { label: "Corregido IA", bg: "#ecfeff", color: "#0e7490", icon: "auto_fix_high" },
  manual:    { label: "Manual",    bg: "#eef2ff", color: "#4338ca", icon: "person_check"    },
  revision:  { label: "Revisar",   bg: "#fdecea", color: "#a33b2e", icon: "warning"         },
  recaptura: { label: "Repetir",   bg: "#fff4e5", color: "#8a5a00", icon: "photo_camera"    },
  pendiente: { label: "Pendiente", bg: "#f1f5f9", color: "#64748b", icon: "hourglass_empty" },
};

export const resolveCedulaBadge = (veredicto: string | null | undefined): ICedulaBadgeConfig => {
  switch (veredicto) {
    case "validado":
    case "completado":
    case "APROBADO":           return CEDULA_BADGE_CONFIG.ok;
    case "corregido_IA":       return CEDULA_BADGE_CONFIG.corregidoIA;
    case "validado_manual":    return CEDULA_BADGE_CONFIG.manual;
    case "requiere_revision":  return CEDULA_BADGE_CONFIG.revision;
    case "requiere_recaptura": return CEDULA_BADGE_CONFIG.recaptura;
    default:                   return CEDULA_BADGE_CONFIG.pendiente;
  }
};

export const cedulaBadgeTooltip = (
  veredicto: string | null | undefined,
  detalle: IDetalleCedulaItem[] | null | undefined,
): string => {
  switch (veredicto) {
    case "validado":
    case "completado":
    case "APROBADO":           return "Los datos coinciden con la cédula.";
    case "corregido_IA":       return "Datos corregidos automáticamente por IA.";
    case "validado_manual":    return "Validado manualmente por un reclutador.";
    case "requiere_revision":  return textoDetalleCedula(detalle);
    case "requiere_recaptura": return "No se pudo leer la cédula. Hay que pedir una foto nueva.";
    default:                   return "La cédula aún no se ha validado.";
  }
};

interface IPillValidacionCedulaProps {
  veredictoCedula?: string | null;
  /** Solo viene poblado cuando el veredicto es "requiere_revision". */
  detalleCedula?: IDetalleCedulaItem[] | null;
}

/** Pill de la columna "Validación". */
const PillValidacionCedula = ({ veredictoCedula, detalleCedula }: IPillValidacionCedulaProps) => {
  const cedulaCfg = resolveCedulaBadge(veredictoCedula);
  return (
    <Tooltip text={cedulaBadgeTooltip(veredictoCedula, detalleCedula)} textTransform="none">
      {/* textTransform none: las tablas de Selección ponen el texto de sus
          celdas en mayúsculas, y el pill tiene que leerse igual que en
          postulaciones. */}
      <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", padding: "4px 10px", borderRadius: "14px", background: cedulaCfg.bg, color: cedulaCfg.color, fontSize: "11.5px", fontWeight: 600, fontFamily: "Roboto, sans-serif", whiteSpace: "nowrap", border: `1px solid ${cedulaCfg.color}22`, cursor: "help", textTransform: "none", lineHeight: "normal" }}>
        <span className="material-symbols-outlined" style={{ fontSize: "15px", lineHeight: 1 }}>{cedulaCfg.icon}</span>
        {cedulaCfg.label}
      </span>
    </Tooltip>
  );
};

export default PillValidacionCedula;
